<template>
  <div class="franquias-metas-tab">
    <!-- Card Principal - Metas Financeiras -->
    <div class="main-card">
      <div class="card-header">
        <div class="header-icon">
          <i class="pi pi-chart-line"></i>
        </div>
        <div class="header-content">
          <h3>{{ t('Metas Financeiras') }}</h3>
          <p>{{ t('Configure os objetivos financeiros da franquia') }}</p>
        </div>
      </div>

      <div class="metas-grid">
        <!-- Card Faturamento Mensal -->
        <div class="metric-card primary">
          <div class="metric-header">
            <i class="pi pi-dollar"></i>
            <span>{{ t('Faturamento Mensal') }}</span>
          </div>
          <div class="metric-input">
            <input-currency
              data-testid="franquias-metas-faturamento-mensal"
              elem-id="faturamento_mensal"
              v-model="localMetas.faturamento_mensal"
              @update:modelValue="handleUpdate"
            />
          </div>
        </div>

        <!-- Card Faturamento Anual -->
        <div class="metric-card success">
          <div class="metric-header">
            <i class="pi pi-money-bill"></i>
            <span>{{ t('Faturamento Anual') }}</span>
          </div>
          <div class="metric-input">
            <input-currency
              data-testid="franquias-metas-faturamento-anual"
              elem-id="faturamento_anual"
              v-model="localMetas.faturamento_anual"
              @update:modelValue="handleUpdate"
            />
          </div>
        </div>

        <!-- Card Crescimento -->
        <div class="metric-card info">
          <div class="metric-header">
            <i class="pi pi-arrow-up"></i>
            <span>{{ t('Crescimento Esperado') }}</span>
          </div>
          <div class="metric-input">
            <input-number
              data-testid="franquias-metas-crescimento"
              elem-id="crescimento"
              suffix="%"
              :min="0"
              :max="100"
              v-model="localMetas.crescimento"
              @update:modelValue="handleUpdate"
            />
          </div>
        </div>

        <!-- Card Ticket Médio -->
        <div class="metric-card warning">
          <div class="metric-header">
            <i class="pi pi-tag"></i>
            <span>{{ t('Ticket Médio') }}</span>
          </div>
          <div class="metric-input">
            <input-currency
              data-testid="franquias-metas-ticket-medio"
              elem-id="ticket_medio"
              v-model="localMetas.ticket_medio"
              @update:modelValue="handleUpdate"
            />
          </div>
        </div>

        <!-- Card Itens/Dia -->
        <div class="metric-card purple">
          <div class="metric-header">
            <i class="pi pi-box"></i>
            <span>{{ t('Itens por Dia') }}</span>
          </div>
          <div class="metric-input">
            <input-number
              data-testid="franquias-metas-itens-dia"
              elem-id="itens_dia"
              :min="0"
              v-model="localMetas.itens_dia"
              @update:modelValue="handleUpdate"
            />
          </div>
        </div>

        <!-- Card Payback (Readonly) -->
        <div class="metric-card readonly">
          <div class="metric-header">
            <i class="pi pi-clock"></i>
            <span>{{ t('Payback Estimado') }}</span>
          </div>
          <div class="metric-value">
            {{ localMetas.payback_meses }} <small>{{ t('meses') }}</small>
          </div>
        </div>
      </div>
    </div>

    <!-- Card de Investimento -->
    <div class="investiment-card">
      <div class="card-header compact">
        <div class="header-icon small">
          <i class="pi pi-wallet"></i>
        </div>
        <div class="header-content">
          <h4>{{ t('Investimento e Retorno') }}</h4>
          <p>{{ t('Configure o investimento inicial e ROI esperado') }}</p>
        </div>
      </div>

      <div class="investiment-grid">
        <div class="investiment-field">
          <label>{{ t('Investimento Total') }}</label>
          <input-currency
            data-testid="franquias-metas-investimento-total"
            elem-id="investimento_total"
            v-model="localInvestimento.investimento_total"
            @update:modelValue="handleUpdate"
          />
        </div>
        <div class="investiment-field">
          <label>{{ t('ROE Desejado') }}</label>
          <input-number
            data-testid="franquias-metas-roe-desejado"
            elem-id="roe_desejado"
            suffix="%"
            :min="0"
            :max="100"
            v-model="localInvestimento.roe_desejado"
            @update:modelValue="handleUpdate"
          />
        </div>
      </div>
    </div>

    <!-- Ações -->
    <div class="actions-section">
      <Button
        data-testid="franquias-metas-save-button"
        :label="t('Salvar Metas')"
        icon="pi pi-save"
        severity="primary"
        size="large"
        :loading="isSaving"
        @click="handleSave"
      />
    </div>

    <!-- Info Card -->
    <div class="info-card">
      <i class="pi pi-lightbulb"></i>
      <div class="info-content">
        <strong>{{ t('Dica') }}</strong>
        <p>{{ t('As metas financeiras são usadas para calcular projeções e analisar o desempenho da franquia. O payback é calculado automaticamente com base no faturamento mensal e investimento total.') }}</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { useFranquias } from '../../composables/useFranquias';
import { log } from '../../utils/DebugLogger';

interface MetasState {
  faturamento_mensal: number | null;
  faturamento_anual: number | null;
  crescimento: number;
  ticket_medio: number | null;
  itens_dia: number | null;
  payback_meses: number;
}

interface InvestimentoState {
  investimento_total: number | null;
  roe_desejado: number | null;
}

interface ConfiguracaoResponse {
  Success?: boolean;
  Dados?: Partial<MetasState> & Partial<InvestimentoState>;
}

interface FranquiaData {
  [key: string]: unknown;
}

interface Props {
  franquiaId: number;
  franquiaData?: FranquiaData | null;
}

const props = withDefaults(defineProps<Props>(), {
  franquiaData: null,
});

const { t } = useI18n();
const { fetchConfiguracaoFranquia, saveMetas } = useFranquias();

// Estado local
const isSaving = ref<boolean>(false);

// Metas calculadas (padrão)
const localMetas = ref<MetasState>({
  faturamento_mensal: null,
  faturamento_anual: null,
  crescimento: 30,
  ticket_medio: null,
  itens_dia: null,
  payback_meses: 0,
});

// Investimento
const localInvestimento = ref<InvestimentoState>({
  investimento_total: null,
  roe_desejado: null,
});

// Calcular payback
const calculatePayback = (): void => {
  const faturamentoMensal = localMetas.value.faturamento_mensal || 0;
  const investimento = localInvestimento.value.investimento_total || 0;

  if (faturamentoMensal > 0) {
    localMetas.value.payback_meses = Math.ceil(investimento / faturamentoMensal);
  } else {
    localMetas.value.payback_meses = 0;
  }
};

// Carregar configuração existente
const loadConfiguracao = async (): Promise<void> => {
  try {
    const config = await fetchConfiguracaoFranquia(props.franquiaId);

    if (config) {
      // Carregar metas
      if (config.faturamento_mensal) localMetas.value.faturamento_mensal = config.faturamento_mensal;
      if (config.faturamento_anual) localMetas.value.faturamento_anual = config.faturamento_anual;
      if (config.crescimento) localMetas.value.crescimento = config.crescimento;
      if (config.ticket_medio) localMetas.value.ticket_medio = config.ticket_medio;
      if (config.itens_dia) localMetas.value.itens_dia = config.itens_dia;

      // Carregar investimento
      if (config.investimento_total) localInvestimento.value.investimento_total = config.investimento_total;
      if (config.roe_desejado) localInvestimento.value.roe_desejado = config.roe_desejado;

      calculatePayback();
    } else {
      // Valores padrão para nova configuração
      localMetas.value = {
        faturamento_mensal: 165000,
        faturamento_anual: 1980000,
        crescimento: 30,
        ticket_medio: 150,
        itens_dia: 50,
        payback_meses: 0,
      };
      localInvestimento.value = {
        investimento_total: 150000,
        roe_desejado: 20,
      };
      calculatePayback();
    }
  } catch {
    // Valores padrão em caso de erro
    localMetas.value = {
      faturamento_mensal: 165000,
      faturamento_anual: 1980000,
      crescimento: 30,
      ticket_medio: 150,
      itens_dia: 50,
      payback_meses: 0,
    };
    calculatePayback();
  }
};

// Handle update
const handleUpdate = (): void => {
  calculatePayback();
};

// Salvar
const handleSave = async (): Promise<void> => {
  isSaving.value = true;

  try {
    const dataToSave: Record<string, unknown> = {
      ...localMetas.value,
      ...localInvestimento.value,
    };
    Object.keys(dataToSave).forEach((key) => {
      if (dataToSave[key] === null) delete dataToSave[key];
    });

    await saveMetas(props.franquiaId, dataToSave as any);

    eventBus.emit('globalMessage.success', t('Metas salvas com sucesso'));
  } catch {
    eventBus.emit('globalMessage.error', t('Erro ao salvar metas'));
  } finally {
    isSaving.value = false;
  }
};

onMounted(async () => {
  try {
    await loadConfiguracao();
  } catch (error) {
    log('FranquiasMetasTab', 'onMounted', 'Erro ao carregar configuração', error);
  }
});

watch(() => [localMetas.value.faturamento_mensal, localInvestimento.value.investimento_total], () => {
  calculatePayback();
}, { deep: true });
</script>

<style scoped>
.franquias-metas-tab {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* Main Card */
.main-card {
  background: var(--surface-card);
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid var(--surface-border);
}

.card-header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 24px;
}

.card-header.compact {
  margin-bottom: 16px;
}

.header-icon {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--primary-color) 0%, color-mix(in srgb, var(--primary-color) 80%, white) 100%);
  border-radius: 12px;
  flex-shrink: 0;
}

.header-icon.small {
  width: 40px;
  height: 40px;
}

.header-icon i {
  font-size: 1.25rem;
  color: white;
}

.header-icon.small i {
  font-size: 1rem;
}

.header-content h3 {
  margin: 0 0 4px 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-color);
}

.header-content h4 {
  margin: 0 0 4px 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-color);
}

.header-content p {
  margin: 0;
  font-size: 0.875rem;
  color: var(--text-color-secondary);
}

/* Metas Grid */
.metas-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

.metric-card {
  background: var(--surface-ground);
  border-radius: 12px;
  padding: 16px;
  border: 1px solid var(--surface-border);
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.metric-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.metric-card.primary {
  border-left: 3px solid #3b82f6;
}

.metric-card.success {
  border-left: 3px solid #22c55e;
}

.metric-card.info {
  border-left: 3px solid #06b6d4;
}

.metric-card.warning {
  border-left: 3px solid #f59e0b;
}

.metric-card.purple {
  border-left: 3px solid #a855f7;
}

.metric-card.readonly {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.04) 0%, rgba(59, 130, 246, 0.02) 100%);
  border-left: 3px solid #6366f1;
}

.metric-header {
  display: flex;
  align-items: center;
  gap: 8px;
}

.metric-header i {
  font-size: 1rem;
  color: var(--primary-color);
}

.metric-header span {
  font-size: 0.75rem;
  font-weight: 600;
  color: #555555;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.metric-input {
  flex: 1;
}

.metric-input :deep(.p-inputtext),
.metric-input :deep(.input-currency) {
  width: 100%;
}

.metric-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--primary-color);
  padding: 8px 0;
}

.metric-value small {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-color-secondary);
}

/* Investiment Card */
.investiment-card {
  background: var(--surface-card);
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid var(--surface-border);
}

.investiment-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.investiment-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.investiment-field label {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-color);
}

.investiment-field :deep(.p-inputtext),
.investiment-field :deep(.input-currency) {
  width: 100%;
}

/* Actions */
.actions-section {
  display: flex;
  justify-content: flex-start;
}

.actions-section :deep(.p-button) {
  padding: 12px 32px;
  font-weight: 600;
  border-radius: 8px;
}

/* Info Card */
.info-card {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 20px;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.06) 0%, rgba(59, 130, 246, 0.02) 100%);
  border: 1px solid rgba(59, 130, 246, 0.15);
  border-radius: 12px;
}

.info-card i {
  color: var(--primary-color);
  font-size: 1.5rem;
  flex-shrink: 0;
  margin-top: 2px;
}

.info-content strong {
  display: block;
  margin-bottom: 6px;
  font-size: 0.938rem;
  color: var(--text-color);
}

.info-content p {
  margin: 0;
  font-size: 0.875rem;
  color: #333333;
  line-height: 1.4;
}

/* Responsive */
@media (max-width: 1024px) {
  .metas-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .metas-grid,
  .investiment-grid {
    grid-template-columns: 1fr;
  }

  .card-header {
    flex-direction: column;
    gap: 12px;
  }

  .header-icon {
    width: 40px;
    height: 40px;
  }
}
</style>
